/**
 * Servicio de analíticas para tracking de eventos de usuario
 */

// Generar un sessionId único que persista durante la sesión
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Detectar tipo de dispositivo
const getDeviceType = (): string => {
  const width = window.innerWidth;
  if (width <= 768) return 'mobile';
  if (width <= 1024) return 'tablet';
  return 'desktop';
};

// Detectar navegador
const getBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  return 'Other';
};

export interface AnalyticsEvent {
  eventType: string;
  eventCategory?: string;
  eventAction?: string;
  eventLabel?: string;
  metadata?: Record<string, any>;
  pagePath?: string;
}

const API_BASE_URL = (() => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  return '/api';
})();

/**
 * Enviar evento de analíticas al backend
 */
export const trackEvent = async (event: AnalyticsEvent): Promise<void> => {
  try {
    const sessionId = getSessionId();
    const deviceType = getDeviceType();
    const browser = getBrowser();
    const pagePath = event.pagePath || window.location.pathname;
    
    await fetch(`${API_BASE_URL}/analytics`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        eventType: event.eventType,
        eventCategory: event.eventCategory,
        eventAction: event.eventAction,
        eventLabel: event.eventLabel,
        metadata: event.metadata,
        sessionId,
        pagePath,
        userAgent: navigator.userAgent,
        deviceType,
        browser
      })
    });
  } catch (error) {
    // Silenciar errores para no interrumpir la experiencia del usuario
    // Error al enviar evento de analíticas - silenciado para no exponer información al usuario
  }
};

/**
 * Eventos predefinidos para facilitar el uso
 */
export const AnalyticsEvents = {
  // Navegación
  profileView: (tab?: string) => trackEvent({
    eventType: 'profile_view',
    eventCategory: 'navigation',
    eventAction: 'view',
    eventLabel: tab,
    metadata: { tab }
  }),

  tabChange: (fromTab: string, toTab: string) => trackEvent({
    eventType: 'tab_change',
    eventCategory: 'navigation',
    eventAction: 'change_tab',
    eventLabel: `${fromTab} -> ${toTab}`,
    metadata: { fromTab, toTab }
  }),

  // Completitud del perfil
  fieldComplete: (fieldName: string, tab: string) => trackEvent({
    eventType: 'field_complete',
    eventCategory: 'profile',
    eventAction: 'complete_field',
    eventLabel: fieldName,
    metadata: { fieldName, tab }
  }),

  profileSave: (isDraft: boolean, completionPercentage: number, hasErrors: boolean) => trackEvent({
    eventType: 'profile_save',
    eventCategory: 'profile',
    eventAction: isDraft ? 'save_draft' : 'save_final',
    eventLabel: `${isDraft ? 'draft' : 'final'}_${completionPercentage}%`,
    metadata: { 
      isDraft, 
      completionPercentage,
      hasErrors
    }
  }),

  profileProgress: (percentage: number, tab: string) => trackEvent({
    eventType: 'profile_progress',
    eventCategory: 'profile',
    eventAction: 'progress_update',
    eventLabel: `${percentage}%`,
    metadata: { percentage, tab }
  }),

  // Validación y errores
  validationError: (errorType: string, fieldName: string, tab: string) => trackEvent({
    eventType: 'validation_error',
    eventCategory: 'validation',
    eventAction: 'error',
    eventLabel: errorType,
    metadata: { errorType, fieldName, tab }
  }),

  clearErrorsAndSave: (errorsCount: number) => trackEvent({
    eventType: 'clear_errors_and_save',
    eventCategory: 'validation',
    eventAction: 'clear_errors',
    eventLabel: `${errorsCount}_errors`,
    metadata: { errorsCount }
  }),

  // Archivos
  fileUpload: (fileType: string, fileSize: number, folder: string) => trackEvent({
    eventType: 'file_upload',
    eventCategory: 'file',
    eventAction: 'upload',
    eventLabel: fileType,
    metadata: { fileType, fileSize, folder }
  }),

  fileUploadError: (errorType: string, fileType?: string) => trackEvent({
    eventType: 'file_upload_error',
    eventCategory: 'file',
    eventAction: 'upload_error',
    eventLabel: errorType,
    metadata: { errorType, fileType }
  }),

  // Chat y mensajería
  chatMessageSent: (hasAttachment: boolean, attachmentType?: string) => trackEvent({
    eventType: 'chat_message_sent',
    eventCategory: 'chat',
    eventAction: 'send_message',
    eventLabel: hasAttachment ? 'with_attachment' : 'text_only',
    metadata: { hasAttachment, attachmentType }
  }),

  chatOpened: () => trackEvent({
    eventType: 'chat_opened',
    eventCategory: 'chat',
    eventAction: 'open'
  }),

  // Compartir y descarga
  profileShared: (method: string) => trackEvent({
    eventType: 'profile_shared',
    eventCategory: 'share',
    eventAction: 'share',
    eventLabel: method,
    metadata: { method }
  }),

  pdfDownloaded: () => trackEvent({
    eventType: 'pdf_downloaded',
    eventCategory: 'download',
    eventAction: 'download_pdf'
  }),

  // Conexión
  connectionError: (duration?: number) => trackEvent({
    eventType: 'connection_error',
    eventCategory: 'connection',
    eventAction: 'error',
    metadata: { duration }
  }),

  connectionRestored: () => trackEvent({
    eventType: 'connection_restored',
    eventCategory: 'connection',
    eventAction: 'restore'
  }),

  // Acciones específicas
  selectAllAxes: (tab: string, axesCount: number) => trackEvent({
    eventType: 'select_all_axes',
    eventCategory: 'interaction',
    eventAction: 'select_all',
    eventLabel: tab,
    metadata: { tab, axesCount }
  }),

  requestProfileReview: () => trackEvent({
    eventType: 'request_profile_review',
    eventCategory: 'profile',
    eventAction: 'request_review'
  }),

  // Sesión
  sessionStart: () => trackEvent({
    eventType: 'session_start',
    eventCategory: 'session',
    eventAction: 'start'
  }),

  sessionEnd: (duration: number) => trackEvent({
    eventType: 'session_end',
    eventCategory: 'session',
    eventAction: 'end',
    metadata: { duration }
  }),

  // Autenticación
  userLogin: (method: string) => trackEvent({
    eventType: 'user_login',
    eventCategory: 'auth',
    eventAction: 'login',
    eventLabel: method,
    metadata: { method }
  }),

  userLogout: () => trackEvent({
    eventType: 'user_logout',
    eventCategory: 'auth',
    eventAction: 'logout'
  }),

  userRegister: (method: string) => trackEvent({
    eventType: 'user_register',
    eventCategory: 'auth',
    eventAction: 'register',
    eventLabel: method,
    metadata: { method }
  }),

  emailVerified: () => trackEvent({
    eventType: 'email_verified',
    eventCategory: 'auth',
    eventAction: 'verify_email'
  }),

  passwordResetRequest: () => trackEvent({
    eventType: 'password_reset_request',
    eventCategory: 'auth',
    eventAction: 'request_reset'
  }),

  passwordResetComplete: () => trackEvent({
    eventType: 'password_reset_complete',
    eventCategory: 'auth',
    eventAction: 'complete_reset'
  }),

  // Archivos y uploads
  fileUploadStart: (fileType: string, fileSize: number, folder: string) => trackEvent({
    eventType: 'file_upload_start',
    eventCategory: 'file',
    eventAction: 'upload_start',
    eventLabel: fileType,
    metadata: { fileType, fileSize, folder }
  }),

  fileUploadSuccess: (fileType: string, fileSize: number, folder: string, uploadTime: number) => trackEvent({
    eventType: 'file_upload_success',
    eventCategory: 'file',
    eventAction: 'upload_success',
    eventLabel: fileType,
    metadata: { fileType, fileSize, folder, uploadTime }
  }),

  fileDelete: (fileType: string, folder: string) => trackEvent({
    eventType: 'file_delete',
    eventCategory: 'file',
    eventAction: 'delete',
    eventLabel: fileType,
    metadata: { fileType, folder }
  }),

  // Interacciones del perfil
  profileFieldChange: (fieldName: string, tab: string, hasValue: boolean) => trackEvent({
    eventType: 'profile_field_change',
    eventCategory: 'profile',
    eventAction: 'field_change',
    eventLabel: fieldName,
    metadata: { fieldName, tab, hasValue }
  }),

  profileSectionComplete: (section: string, completionPercentage: number) => trackEvent({
    eventType: 'profile_section_complete',
    eventCategory: 'profile',
    eventAction: 'section_complete',
    eventLabel: section,
    metadata: { section, completionPercentage }
  }),

  profileAbandon: (lastTab: string, completionPercentage: number, timeSpent: number) => trackEvent({
    eventType: 'profile_abandon',
    eventCategory: 'profile',
    eventAction: 'abandon',
    eventLabel: lastTab,
    metadata: { lastTab, completionPercentage, timeSpent }
  }),

  tabTimeSpent: (tab: string, timeSpent: number) => trackEvent({
    eventType: 'tab_time_spent',
    eventCategory: 'navigation',
    eventAction: 'time_spent',
    eventLabel: tab,
    metadata: { tab, timeSpent }
  }),

  // Errores y problemas
  rateLimitHit: (endpoint: string) => trackEvent({
    eventType: 'rate_limit_hit',
    eventCategory: 'error',
    eventAction: 'rate_limit',
    eventLabel: endpoint,
    metadata: { endpoint }
  }),

  apiError: (endpoint: string, statusCode: number, errorMessage: string) => trackEvent({
    eventType: 'api_error',
    eventCategory: 'error',
    eventAction: 'api_error',
    eventLabel: `${endpoint}_${statusCode}`,
    metadata: { endpoint, statusCode, errorMessage }
  }),

  validationErrorShown: (errorType: string, fieldName: string, tab: string) => trackEvent({
    eventType: 'validation_error_shown',
    eventCategory: 'validation',
    eventAction: 'error_shown',
    eventLabel: errorType,
    metadata: { errorType, fieldName, tab }
  }),

  // Acciones de usuario
  modalShown: (modalType: string) => trackEvent({
    eventType: 'modal_shown',
    eventCategory: 'interaction',
    eventAction: 'show_modal',
    eventLabel: modalType,
    metadata: { modalType }
  }),

  modalAction: (modalType: string, action: string) => trackEvent({
    eventType: 'modal_action',
    eventCategory: 'interaction',
    eventAction: 'modal_action',
    eventLabel: `${modalType}_${action}`,
    metadata: { modalType, action }
  }),

  buttonClick: (buttonName: string, location: string) => trackEvent({
    eventType: 'button_click',
    eventCategory: 'interaction',
    eventAction: 'click',
    eventLabel: buttonName,
    metadata: { buttonName, location }
  }),

  // Compartir y exportar
  profileShareClick: (method: string) => trackEvent({
    eventType: 'profile_share_click',
    eventCategory: 'share',
    eventAction: 'click',
    eventLabel: method,
    metadata: { method }
  }),

  profileShareSuccess: (method: string) => trackEvent({
    eventType: 'profile_share_success',
    eventCategory: 'share',
    eventAction: 'share_success',
    eventLabel: method,
    metadata: { method }
  }),

  pdfGenerateStart: () => trackEvent({
    eventType: 'pdf_generate_start',
    eventCategory: 'export',
    eventAction: 'generate_start'
  }),

  pdfGenerateSuccess: (generationTime: number) => trackEvent({
    eventType: 'pdf_generate_success',
    eventCategory: 'export',
    eventAction: 'generate_success',
    metadata: { generationTime }
  }),

  pdfGenerateError: (error: string) => trackEvent({
    eventType: 'pdf_generate_error',
    eventCategory: 'export',
    eventAction: 'generate_error',
    metadata: { error }
  }),

  // Chat y mensajería adicionales
  chatMessageReceived: (hasAttachment: boolean) => trackEvent({
    eventType: 'chat_message_received',
    eventCategory: 'chat',
    eventAction: 'receive_message',
    eventLabel: hasAttachment ? 'with_attachment' : 'text_only',
    metadata: { hasAttachment }
  }),

  chatAttachmentDownload: (fileType: string) => trackEvent({
    eventType: 'chat_attachment_download',
    eventCategory: 'chat',
    eventAction: 'download_attachment',
    eventLabel: fileType,
    metadata: { fileType }
  }),

  // Admin actions (si el usuario es admin)
  adminAction: (action: string, targetType: string, targetId?: number) => trackEvent({
    eventType: 'admin_action',
    eventCategory: 'admin',
    eventAction: action,
    eventLabel: targetType,
    metadata: { action, targetType, targetId }
  }),

  // Performance
  pageLoadTime: (page: string, loadTime: number) => trackEvent({
    eventType: 'page_load_time',
    eventCategory: 'performance',
    eventAction: 'load',
    eventLabel: page,
    metadata: { page, loadTime }
  }),

  apiCallTime: (endpoint: string, method: string, duration: number, success: boolean) => trackEvent({
    eventType: 'api_call_time',
    eventCategory: 'performance',
    eventAction: 'api_call',
    eventLabel: `${method}_${endpoint}`,
    metadata: { endpoint, method, duration, success }
  })
};
